import ExcelJS from 'exceljs';
import { getCollaborators } from '@/features/Collaborators/Services/collaboratorPrivateService';
import { fetchCapabilityCatalog } from '@/features/Collaborators/Data/hooks/useCapabilityCatalogQuery';
import type { CollaboratorListRow } from '../exportsPage.types';
import { buildCollaboratorListRows, downloadWorkbook, toMonthKey } from '../utils';
import { fetchAllocationResponsibles } from './allocationsData';

const DATE_FORMAT = 'dd/mm/yyyy';

/** Monta a planilha de listagem completa de colaboradores. */
function buildWorkbook(rows: CollaboratorListRow[]): ExcelJS.Workbook {
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet('Colaboradores');

    worksheet.columns = [
        { header: 'ID', key: 'id', width: 30 },
        { header: 'Nome', key: 'name', width: 36 },
        { header: 'Situação', key: 'situation', width: 16 },
        { header: 'Senioridade', key: 'seniorityName', width: 18 },
        { header: 'Habilidade Principal', key: 'primarySkill', width: 30 },
        { header: 'Vínculo', key: 'employmentType', width: 14 },
        { header: 'Data de Admissão', key: 'admissionDate', width: 18 },
        { header: 'Data de Demissão', key: 'terminationDate', width: 18 },
        { header: 'Motivo do Desligamento', key: 'terminationReason', width: 32 },
        { header: 'Líder Técnico', key: 'technicalLeader', width: 30 },
        { header: 'Cliente Alocado', key: 'allocatedClient', width: 32 },
    ];

    worksheet.getRow(1).font = { bold: true };

    rows.forEach((row) => {
        worksheet.addRow(row);
    });

    worksheet.getColumn('admissionDate').numFmt = DATE_FORMAT;
    worksheet.getColumn('terminationDate').numFmt = DATE_FORMAT;

    worksheet.autoFilter = { from: 'A1', to: 'K1' };
    worksheet.views = [{ state: 'frozen', xSplit: 2, ySplit: 1 }];

    worksheet.getCell('C1').note = {
        texts: [
            {
                font: { size: 10, name: 'Calibri', family: 2 },
                text:
                    'Situação, na ordem de avaliação (a primeira que casar vence):\n\n' +
                    'Desativado: status do cadastro inativo ou desligado.\n' +
                    'Interno: tem alocação vigente do tipo Interno ou Comercial.\n' +
                    'Alocado: tem alocação vigente em cliente.\n' +
                    'Desalocado: nenhuma alocação vigente.',
            },
        ],
        margins: { insetmode: 'custom', inset: [0.25, 0.25, 0.35, 0.35] },
    };

    worksheet.getCell('I1').note = {
        texts: [
            {
                font: { size: 10, name: 'Calibri', family: 2 },
                text:
                    'Coluna reservada: ainda não existe campo de motivo de desligamento no cadastro do colaborador.\n' +
                    'Preencher manualmente até que o campo seja criado.',
            },
        ],
        margins: { insetmode: 'custom', inset: [0.25, 0.25, 0.35, 0.35] },
    };

    worksheet.getCell('J1').note = {
        texts: [
            {
                font: { size: 10, name: 'Calibri', family: 2 },
                text:
                    'Origem: "Responsável Raro" da alocação vigente (alocação em cliente tem prioridade).\n' +
                    'Sem alocação vigente, ou alocação sem responsável: cai para o "Responsável Raro" do cadastro do colaborador.',
            },
        ],
        margins: { insetmode: 'custom', inset: [0.25, 0.25, 0.35, 0.35] },
    };

    return workbook;
}

/**
 * Exporta a listagem completa de colaboradores. Retorna quantas linhas foram
 * geradas.
 */
export async function exportCollaboratorsList(): Promise<number> {
    const [collaborators, catalog] = await Promise.all([
        getCollaborators({ orderByField: 'name', orderDirection: 'asc' }),
        fetchCapabilityCatalog(),
    ]);

    const allocationResponsibles = await fetchAllocationResponsibles(
        collaborators.map((collaborator) => collaborator.id)
    );

    const rows = buildCollaboratorListRows(collaborators, catalog, allocationResponsibles);
    const workbook = buildWorkbook(rows);

    await downloadWorkbook(workbook, `colaboradores-lista-${toMonthKey(new Date())}.xlsx`);

    return rows.length;
}
